/**
 * Rendering Engine — paint resolution.
 *
 * Fills, gradients, strokes, shadows, blurs, glows, glass, grain and blend
 * modes → the editor's native painting properties. Anything the editor has no
 * native slot for is preserved under `meta` rather than dropped.
 *
 * Pure and deterministic.
 */

import { SHADOW_DEFAULTS, GLOW_DEFAULTS, GLASS_DEFAULTS } from './rendererSchema';

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const asArray = (v) => (Array.isArray(v) ? v : []);

/** A gradient fill → the CSS the editor's background/gradient slots understand. */
export const gradientToCss = (fill, tokens) => {
  const stops = asArray(fill.stops).length
    ? asArray(fill.stops).map((s, i, all) => `${tokens.color(s.color)} ${isNum(s.at) ? Math.round(s.at * 100) : Math.round((i / Math.max(1, all.length - 1)) * 100)}%`)
    : [tokens.color(fill.color, '#000000'), tokens.color(fill.to, '#ffffff')].map((c, i) => `${c} ${i * 100}%`);
  const angle = isNum(fill.angle) ? fill.angle : 90;

  switch (fill.gradient) {
    case 'radial': return `radial-gradient(circle, ${stops.join(', ')})`;
    case 'conic': return `conic-gradient(from ${angle}deg, ${stops.join(', ')})`;
    case 'mesh': // the editor has no mesh gradient: a multi-stop linear is the honest approximation
    case 'linear':
    default: return `linear-gradient(${angle}deg, ${stops.join(', ')})`;
  }
};

/** The colours a gradient uses, for the editor's `fillGradient` slot. */
const gradientColors = (fill, tokens) => (asArray(fill.stops).length
  ? asArray(fill.stops).map((s) => tokens.color(s.color))
  : [tokens.color(fill.color, '#000000'), tokens.color(fill.to, '#ffffff')]);

/**
 * A fill stack → the editor's paint properties for one element.
 * The first paintable fill wins; the rest are returned for the caller to
 * render as their own layers (that is what a background SYSTEM means).
 *
 * @returns {{ props: Object, extra: Object[] }}
 */
export const resolveFills = (fills, tokens) => {
  const list = asArray(fills).filter(isObj);
  if (list.length === 0) return { props: {}, extra: [] };

  const [first, ...rest] = list;
  const props = {};

  switch (first.kind) {
    case 'solid':
      props.fill = tokens.color(first.color, 'transparent');
      break;
    case 'gradient':
      props.fill = gradientColors(first, tokens)[0];
      props.fillGradient = { colors: gradientColors(first, tokens), angle: isNum(first.angle) ? first.angle : 90, css: gradientToCss(first, tokens) };
      break;
    case 'image':
      props.src = first.src || null;
      props.objectFit = first.fit || 'cover';
      break;
    case 'pattern':
      props.fill = tokens.color(first.color, 'transparent');
      props.pattern = first.pattern || null;
      break;
    case 'noise':
    case 'texture':
      props.fill = tokens.color(first.color, 'transparent');
      props.texture = first.pattern || first.kind;
      break;
    case 'none':
    default:
      props.fill = 'transparent';
      break;
  }

  if (isNum(first.opacity) && first.opacity !== 1) props.fillOpacity = first.opacity;
  if (first.blend && first.blend !== 'normal') props.blendMode = first.blend;

  return { props, extra: rest };
};

/** A stroke → the editor's border properties. */
export const resolveStroke = (stroke, tokens) => {
  if (!isObj(stroke) || !isNum(stroke.width) || stroke.width <= 0) return { stroke: 'none', strokeWidth: 0 };
  const fill = stroke.fill;
  const color = isObj(fill) ? tokens.color(fill.color, '#000000') : tokens.color(fill, '#000000');
  const props = { stroke: color, strokeWidth: stroke.width };
  if (stroke.style && stroke.style !== 'solid') props.strokeStyle = stroke.style;
  if (stroke.align && stroke.align !== 'center') props.strokeAlign = stroke.align;
  return props;
};

/**
 * An effect stack → the editor's native effect properties.
 * Every effect kind in the schema is handled; nothing is silently dropped.
 */
export const resolveEffects = (effects, tokens) => {
  const props = {};
  const unsupported = [];

  asArray(effects).filter(isObj).forEach((effect) => {
    switch (effect.kind) {
      case 'shadow':
      case 'drop-shadow':
        props.shadowOffsetX = isNum(effect.x) ? effect.x : SHADOW_DEFAULTS.x;
        props.shadowOffsetY = isNum(effect.y) ? effect.y : SHADOW_DEFAULTS.y;
        props.shadowBlur = isNum(effect.blur) ? effect.blur : SHADOW_DEFAULTS.blur;
        props.shadowColor = tokens.color(effect.color, SHADOW_DEFAULTS.color);
        break;
      case 'inner-shadow':
        props.innerShadow = {
          x: isNum(effect.x) ? effect.x : 0, y: isNum(effect.y) ? effect.y : SHADOW_DEFAULTS.y,
          blur: isNum(effect.blur) ? effect.blur : SHADOW_DEFAULTS.blur,
          color: tokens.color(effect.color, SHADOW_DEFAULTS.color),
        };
        break;
      case 'glow':
        // A glow is a shadow with no offset — the editor renders it natively.
        props.shadowOffsetX = 0;
        props.shadowOffsetY = 0;
        props.shadowBlur = isNum(effect.blur) ? effect.blur : GLOW_DEFAULTS.blur;
        props.shadowColor = tokens.color(effect.color, GLOW_DEFAULTS.color);
        break;
      case 'blur':
        props.blur = isNum(effect.amount) ? effect.amount : (isNum(effect.blur) ? effect.blur : 8);
        break;
      case 'backdrop-blur':
      case 'glass':
        props.backdropBlur = isNum(effect.blur) ? effect.blur : GLASS_DEFAULTS.blur;
        props.glass = true;
        if (props.fill === undefined) props.fill = tokens.color(effect.color, GLASS_DEFAULTS.tint);
        if (props.fillOpacity === undefined) props.fillOpacity = isNum(effect.opacity) ? effect.opacity : GLASS_DEFAULTS.opacity;
        break;
      case 'grain':
      case 'noise':
        props.texture = 'grain';
        props.textureAmount = isNum(effect.amount) ? effect.amount : 0.2;
        break;
      case 'opacity':
        if (isNum(effect.amount)) props.opacity = effect.amount;
        break;
      case 'blend':
        if (effect.mode) props.blendMode = effect.mode;
        break;
      case 'stroke':
        props.stroke = tokens.color(effect.color, '#000000');
        props.strokeWidth = isNum(effect.amount) ? effect.amount : 1;
        break;
      default:
        // Unknown to the editor today; recorded so nothing is lost.
        unsupported.push(effect);
        break;
    }
  });

  return { props, unsupported };
};
